/**
 * 首页「推荐曲库」的曲目目录。
 *
 * 曲谱本体是 `public/examples/*.spnmn`（从 `packages/website/static/playground/example/`
 * 拷来的真实示例，与官网示例页同源），运行时按需 fetch —— 首页不预载任何一首，
 * 点开哪首才取哪首。
 *
 * 为什么是静态文件而不是 Supabase 表：现在没有「公开曲谱」这张表，为几首固定曲目
 * 建表 + 配 RLS 不划算。以后要让运营后台维护，再换数据源即可，卡片这层不用动。
 *
 * `blurb` 是"为什么值得点开"，不是功能清单 —— 每首都对着自己在编辑器里的样子写，
 * 别写成三张一样的"示例曲谱"。
 */
export type ExampleScore = {
	/** `public/examples/` 下的文件名，含扩展名 */
	file: string
	title: string
	type: string
	blurb: string
}

export const EXAMPLE_SCORES: ExampleScore[] = [
	{
		file: '小星星.spnmn',
		title: '小星星',
		type: '入门儿歌',
		blurb: '最短的一份谱，第一次打开就能看懂每行在写什么',
	},
	{
		file: '小星星-柱式和弦.spnmn',
		title: '小星星（柱式和弦）',
		type: '入门儿歌',
		blurb: '左手一个方括号同时按 1 3 5，同一首曲子只换左手写法，对照着听',
	},
	{
		file: '数鸭子.spnmn',
		title: '数鸭子',
		type: '入门儿歌',
		blurb: '一句词竖着排在几个音下面，看清歌词是怎么对上的',
	},
	{
		file: '小手拉大手.spnmn',
		title: '小手拉大手',
		type: '流行歌曲',
		blurb: '反复记号与连音线，流行歌里最常见的写法都在这',
	},
	{
		file: '鲁冰花.spnmn',
		title: '鲁冰花',
		type: '经典儿歌',
		blurb: '中途转调，点开五线谱预览能听出调性换了',
	},
	{
		file: '安娜的橱窗.spnmn',
		title: '安娜的橱窗',
		type: '流行歌曲',
		blurb: '摇摆节奏加变化音，看起来比唱起来简单',
	},
	{
		file: 'Let It Go.spnmn',
		title: 'Let It Go',
		type: '影视歌曲',
		blurb: '多声部、变拍、带中文翻译的歌词，一份完整的编制',
	},
]

/**
 * 按文件名找曲目（官网「启动 / 试一试」链接的 `?load-example=<name>.spnmn` 参数走这里）。
 *
 * 官网的链接是拿 `packages/website/static/playground/example/` 下的文件名拼的
 * （含 `version-poc/` 前缀），本站 `public/examples/` 只同步了其中 7 首 ——
 * 找不到就返回 undefined，调用方落到编辑器的默认新谱，别报错吓人。
 */
export function findExampleByFile(file: string): ExampleScore | undefined {
	return EXAMPLE_SCORES.find(item => item.file === file)
}

/**
 * 取曲谱正文（按 `public/examples/` 下的相对路径）。
 *
 * ⚠️ 不能把整串丢给 `encodeURIComponent` —— 曲库的路径带子目录
 * （`library/etude-0001.spnmn`），整串编码会把 `/` 变成 `%2F`，静态服务器
 * 当成文件名找，一律 404。只能**逐段**编码。
 *
 * 走**相对路径**（与 `getExportTemplate` 取 `./static/export-template.txt` 同一套约定）：
 * 生产构建是 `--base=./`，哈希路由又从不改 URL 的目录部分，所以相对路径始终对着
 * index.html 所在目录解析，dev 与子目录部署都对。写成 `'/examples/…'` 或
 * `import.meta.env.BASE_URL` 反而要多引一份 vite/client 类型（本包没装）。
 */
export async function fetchScoreFile(file: string): Promise<string> {
	const path = file.split('/').map(encodeURIComponent).join('/')
	const res = await fetch(`./examples/${path}`)
	if (!res.ok) throw new Error(`曲谱读取失败（${res.status}）`)
	return await res.text()
}

export async function fetchExampleScore(item: ExampleScore): Promise<string> {
	return await fetchScoreFile(item.file)
}
